import React from 'react';

function PreviewSubscriptionChangeModal({ isOpen, onClose, onConfirm, previewData, isLoading, error, isConfirming }) {
    if (!isOpen) return null;

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
            {/* Backdrop */}
            <div 
                className="fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity"
                onClick={isConfirming ? undefined : onClose}
            />

            {/* Modal Container */}
            <div className="flex min-h-full items-center justify-center p-3 sm:p-4">
                <div className="relative bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-xl sm:rounded-2xl shadow-2xl max-w-lg w-full border border-slate-800/50 max-h-[90vh] overflow-y-auto">
                    {/* Header */}
                    <div className="border-b border-slate-800/50 px-4 sm:px-5 py-3 sm:py-3.5">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg sm:text-xl font-bold text-white">
                                {isConfirming ? 'Processing...' : isLoading ? 'Loading...' : error ? 'Error' : 
                                    previewData?.change_type === 'upgrade' ? 'Confirm Upgrade' : 'Confirm Downgrade'}
                            </h2>
                            <button
                                onClick={onClose}
                                disabled={isConfirming}
                                className="text-slate-400 hover:text-white transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-4 sm:p-5">
                        {isConfirming ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="text-center">
                                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent mb-4"></div>
                                    <p className="text-lg font-semibold text-white mb-2">Processing subscription change...</p>
                                    <p className="text-sm text-slate-400">This will take a few moments while we update your subscription.</p>
                                </div>
                            </div>
                        ) : isLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="text-center">
                                    <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-emerald-500 border-t-transparent"></div>
                                    <p className="mt-3 text-sm text-slate-400">Loading preview...</p>
                                </div>
                            </div>
                        ) : error ? (
                            <div className="text-center py-6">
                                <div className="text-red-400 mb-3">
                                    <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <p className="font-semibold text-white mb-1">Unable to Preview Change</p>
                                <p className="text-sm text-slate-400">{error}</p>
                            </div>
                        ) : previewData ? (
                            <div className="space-y-4">
                                {/* Plan Change Summary */}
                                <div className="bg-slate-800/30 rounded-xl p-3.5 border border-slate-700/50">
                                    <div className="grid grid-cols-[1fr,auto,1fr] items-center gap-3 mb-3">
                                        <div className="text-center">
                                            <p className="text-xs text-slate-400 mb-0.5">From</p>
                                            <p className="font-bold text-white">{previewData.current_plan.display_name}</p>
                                            <p className="text-xs text-emerald-400">${parseFloat(previewData.current_plan.monthly_price).toFixed(2)}/mo</p>
                                        </div>
                                        
                                        <div className="flex items-center justify-center">
                                            <svg className="w-6 h-6 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                            </svg>
                                        </div>

                                        <div className="text-center">
                                            <p className="text-xs text-slate-400 mb-0.5">To</p>
                                            <p className="font-bold text-white">{previewData.new_plan.display_name}</p>
                                            <p className="text-xs text-emerald-400">${parseFloat(previewData.new_plan.monthly_price).toFixed(2)}/mo</p>
                                        </div>
                                    </div>

                                    {/* Change Type Badge */}
                                    <div className="flex items-center justify-center pt-2 border-t border-slate-700/30">
                                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-semibold ${
                                            previewData.change_type === 'upgrade'
                                                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                                : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                                        }`}>
                                            {previewData.change_type === 'upgrade' ? (
                                                <>
                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                                    </svg>
                                                    Effective Immediately
                                                </>
                                            ) : (
                                                <>
                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    End of Billing Period
                                                </>
                                            )}
                                        </span>
                                    </div>
                                </div>

                                {/* Pricing Details */}
                                <div className="bg-slate-800/30 rounded-xl p-3.5 border border-slate-700/50">
                                    <h3 className="font-semibold text-white mb-2.5 text-sm">Pricing Summary</h3>
                                    
                                    {/* Description */}
                                    <div className="bg-slate-900/50 rounded-lg p-3 mb-3">
                                        <p className="text-xs text-slate-300 leading-relaxed">
                                            {previewData.pricing.description}
                                        </p>
                                    </div>

                                    <div className="space-y-2">
                                        {/* Immediate Charge */}
                                        <div className="flex items-center justify-between py-2 border-t border-slate-700/30">
                                            <span className="text-xs font-medium text-slate-300">
                                                {previewData.change_type === 'upgrade' ? 'Due Today' : 'Immediate Charge'}
                                            </span>
                                            <span className={`text-base font-bold ${
                                                parseFloat(previewData.pricing.immediate_charge) > 0 
                                                    ? 'text-emerald-400' 
                                                    : 'text-slate-400'
                                            }`}>
                                                {previewData.pricing.currency} ${parseFloat(previewData.pricing.immediate_charge).toFixed(2)}
                                            </span>
                                        </div>

                                        {/* Proration Details (Upgrades Only) */}
                                        {previewData.change_type === 'upgrade' && previewData.pricing.proration_details && previewData.pricing.proration_details.length > 0 && (
                                            <div className="bg-slate-900/30 rounded p-2 space-y-1.5 mt-2">
                                                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Breakdown</p>
                                                {previewData.pricing.proration_details.map((detail, index) => (
                                                    <div key={index} className="flex items-center justify-between text-xs">
                                                        <span className="text-slate-400">{detail.description}</span>
                                                        <span className={parseFloat(detail.amount) < 0 ? 'text-red-400' : 'text-emerald-400'}>
                                                            {parseFloat(detail.amount) < 0 ? '-' : '+'}{previewData.pricing.currency} ${Math.abs(parseFloat(detail.amount)).toFixed(2)}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Next Billing */}
                                        <div className="flex items-center justify-between py-2 border-t border-slate-700/30">
                                            <span className="text-xs font-medium text-slate-300">Next Monthly Charge</span>
                                            <span className="text-base font-bold text-white">
                                                {previewData.pricing.currency} ${parseFloat(previewData.billing.next_billing_amount).toFixed(2)}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Important Info */}
                                {previewData.change_type === 'upgrade' ? (
                                    <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
                                        <div className="flex items-start gap-2">
                                            <svg className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                            </svg>
                                            <div>
                                                <p className="text-xs text-emerald-300 leading-relaxed">
                                                    Your upgrade will be applied immediately and you'll have access to all {previewData.new_plan.display_name} features right away.
                                                </p>
                                                <p className="text-[10px] text-emerald-400/70 mt-1">
                                                    Current period ends: {formatDate(previewData.billing.current_period_end)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                                        <div className="flex items-start gap-2">
                                            <svg className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <div>
                                                <p className="text-xs text-blue-300 leading-relaxed">
                                                    You'll continue to have access to {previewData.current_plan.display_name} until {formatDate(previewData.billing.current_period_end)}.
                                                </p>
                                                <p className="text-[10px] text-blue-400/70 mt-1">
                                                    Downgrade effective: {formatDate(previewData.billing.downgrade_effective_date || previewData.billing.current_period_end)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : null}
                    </div>

                    {/* Footer Actions */}
                    {!isLoading && !error && !isConfirming && previewData && (
                        <>
                            {/* Stripe Security Badge */}
                            <div className="border-t border-slate-800/50 px-4 sm:px-5 py-2 sm:py-2.5">
                                <div className="flex items-center justify-center gap-1.5 text-[11px] sm:text-xs text-slate-400">
                                    <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                    <span>Securely processed by <span className="font-semibold text-slate-300">Stripe</span></span>
                                </div>
                            </div>
                            
                            <div className="border-t border-slate-800/50 px-4 sm:px-5 py-3 sm:py-3.5 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-2.5">
                                <button
                                    onClick={onClose}
                                    className="px-4 sm:px-5 py-2 rounded-lg text-sm font-medium text-slate-300 hover:text-white bg-slate-800/50 hover:bg-slate-700/50 transition-colors duration-200 order-2 sm:order-1"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={onConfirm}
                                    className="px-4 sm:px-5 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 transition-all duration-200 shadow-lg shadow-emerald-500/20 order-1 sm:order-2"
                                >
                                    {previewData.change_type === 'upgrade' ? 'Confirm Upgrade' : 'Confirm Downgrade'}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default PreviewSubscriptionChangeModal;

